/**
 * useDualCtaVariant
 *
 * Shared hook that encapsulates the canonical resolution cascade for a
 * dual-CTA A/B variant:
 *
 *   1. liveCTA.dualVariant (from worker snapshot poll + admin broadcast)
 *   2. spaResolvedVariant (client-nav fallback via /api/dual-cta-variant-resolved)
 *   3. workerVariant (__TEMPLATE_CONFIG__.dualVariant seeded on hard nav)
 *
 * Also handles:
 * - Sticky abBucket (from server config)
 * - SPA fallback only when no worker seed and a dual_ab_bucket cookie exists
 * - Skips SPA resolve when ?_force_variant= (admin preview)
 *
 * Consumers: DualCTAVideoLanding, USExclusiveLanding, VideoTemplate (US/intl
 * con variante A/B). BaseTemplate NO usa este hook — solo hero CTA vía useLiveCTA.
 *
 * The hook intentionally stays lightweight: it does not fetch labels/visibility
 * (useDualCtaLabels / useDualCtaVisibility remain separate for surface concerns).
 *
 * NOTE on presentation layer (migración 064):
 *   The `variant.presentation` blob is only rendered inside DualCTAVideoLanding.
 *   Other templates do not surface per-variant theming currently.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  getServerTemplateConfig,
  type DualVariantConfig,
} from "@/lib/serverTemplateDetection";
import { useDualCtaLive, type LiveCTAState } from "@/contexts/DualCtaLiveContext";

export interface DualCtaVariantResult {
  /** The effective resolved variant (live wins > spa > worker seed) */
  variant: DualVariantConfig | undefined;
  /** Sticky bucket id (for analytics / checkout metadata). Always provided when hook is used. */
  abBucket: string | undefined;
  /**
   * Shared dual-CTA snapshot (DualCtaLiveProvider). Solo membresía/US.
   */
  liveCTA: LiveCTAState;
}

interface UseDualCtaVariantOptions {
  /**
   * Language base to send to the resolver for label pick (es/en/pt/de/fr/it).
   * Falls back to server config language or "es".
   */
  resolveLang?: string;
}

/**
 * Read the dual_ab_bucket cookie (set by the worker on every response).
 * Returns null when absent or in SSR.
 */
function readDualAbBucketCookie(): string | null {
  if (typeof document === "undefined") return null;
  try {
    return (
      document.cookie
        .split(";")
        .map((c) => c.trim())
        .find((c) => c.startsWith("dual_ab_bucket="))
        ?.split("=")[1] ?? null
    );
  } catch {
    return null;
  }
}

export function useDualCtaVariant(
  options: UseDualCtaVariantOptions = {},
): DualCtaVariantResult {
  const { resolveLang } = options;

  const workerVariant = useMemo(
    () => getServerTemplateConfig()?.dualVariant,
    [],
  );
  const abBucket = useMemo(
    () => getServerTemplateConfig()?.abBucket,
    [],
  );

  const liveCTA = useDualCtaLive();
  const [spaResolvedVariant, setSpaResolvedVariant] = useState<
    DualVariantConfig | undefined
  >();

  const variant = useMemo((): DualVariantConfig | undefined => {
    if (liveCTA.dualVariant) return liveCTA.dualVariant as DualVariantConfig;
    if (spaResolvedVariant) return spaResolvedVariant;
    return workerVariant;
  }, [liveCTA.dualVariant, spaResolvedVariant, workerVariant]);

  // Si el poll live trae una variante distinta, descartamos el fallback SPA
  // (evita estado obsoleto cuando el admin reasigna geos en caliente).
  useEffect(() => {
    if (!liveCTA.dualVariant?.id) return;
    setSpaResolvedVariant((prev) =>
      prev && prev.id !== liveCTA.dualVariant?.id ? undefined : prev,
    );
  }, [liveCTA.dualVariant?.id]);

  // Snapshot values at mount time so the effect doesn't suffer stale closures
  // and only decides SPA once (same behavior as the previous duplicated code).
  const shouldAttemptSpaRef = useRef<boolean | null>(null);
  if (shouldAttemptSpaRef.current === null) {
    shouldAttemptSpaRef.current = !workerVariant;
  }

  // SPA fallback effect (only runs when we didn't get a seed from the worker)
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Admin preview forces a concrete variant — do not let the normal
    // bucket-based resolver stomp it on the next poll or SPA fetch.
    const search = new URLSearchParams(window.location.search);
    if (search.get("_force_variant")) return;

    if (!shouldAttemptSpaRef.current) return; // seed present on hard nav
    if (typeof document === "undefined") return;

    const bucketFromCookie = readDualAbBucketCookie();
    if (!bucketFromCookie) return;

    const cfg = getServerTemplateConfig();
    const country =
      cfg?.country && cfg.country !== "UNKNOWN" ? cfg.country : "";
    if (!country) return;

    // Resolve lang for label selection in the API (base code only).
    // Prefer explicit option, then server-injected language, then "es".
    let langForResolve = (resolveLang || "").trim().toLowerCase();
    if (!langForResolve) {
      const serverLang = (cfg?.language || "").split("-")[0] || "es";
      langForResolve = serverLang.toLowerCase();
    }

    const params = new URLSearchParams({
      country,
      bucket: bucketFromCookie,
      lang: langForResolve,
    });
    if (cfg?.province) params.set("province", cfg.province);
    if (cfg?.city) params.set("city", cfg.city);
    if (cfg?.usState) params.set("state", cfg.usState);
    // Path actual — scope landing_path (paridad con worker hard-nav/snapshot).
    params.set("path", window.location.pathname);

    let cancelled = false;
    void fetch(`/api/dual-cta-variant-resolved?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { variant?: DualVariantConfig | null } | null) => {
        if (cancelled) return;
        if (data?.variant) setSpaResolvedVariant(data.variant);
      })
      .catch(() => {
        /* best effort */
      });

    return () => {
      cancelled = true;
    };
    // Intentionally no deps — effect runs once per mount (SPA fallback only).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    variant,
    abBucket,
    liveCTA,
  };
}
